import { useState } from "react";
import { Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ExpenseCategory, IncomeCategory, TransactionType } from "@boo/shared";
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  EXPENSE_CATEGORIES,
  INCOME_CATEGORY_COLORS,
  INCOME_CATEGORY_LABELS,
} from "../../types";

const INCOME_CATEGORIES: IncomeCategory[] = [
  IncomeCategory.SALARY,
  IncomeCategory.BONUS,
  IncomeCategory.FREELANCE,
  IncomeCategory.BUSINESS,
  IncomeCategory.INVESTMENT,
  IncomeCategory.RENTAL,
  IncomeCategory.OTHER,
];

interface CategoryEditorProps {
  transactionId: string;
  currentCategory: ExpenseCategory | IncomeCategory | null;
  transactionType: TransactionType;
  isLarge?: boolean;
  onCategorize: (
    id: string,
    category: ExpenseCategory | IncomeCategory,
  ) => void;
}

export default function CategoryEditor({
  transactionId,
  currentCategory,
  transactionType,
  isLarge = false,
  onCategorize,
}: CategoryEditorProps) {
  const [open, setOpen] = useState(false);
  const [flashing, setFlashing] = useState(false);

  const isIncome = transactionType === TransactionType.INCOME;
  const categories = isIncome ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const colorMap = isIncome ? INCOME_CATEGORY_COLORS : CATEGORY_COLORS;
  const labelMap = isIncome ? INCOME_CATEGORY_LABELS : CATEGORY_LABELS;

  const handleSelect = (cat: ExpenseCategory | IncomeCategory) => {
    onCategorize(transactionId, cat);
    setOpen(false);
    setFlashing(true);
    setTimeout(() => setFlashing(false), 600);
  };

  const catKey = currentCategory?.toLowerCase();
  const bg = catKey
    ? `var(--cat-${catKey}-bg, var(--border-color))`
    : "var(--border-color)";
  const color = catKey
    ? `var(--cat-${catKey}-text, var(--text-secondary))`
    : "var(--text-secondary)";
  const label = !currentCategory
    ? "pick category"
    : ((labelMap as Record<string, string>)[currentCategory]?.toLowerCase() ??
      currentCategory.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium border border-dashed border-transparent transition-all rounded-[6px] cursor-pointer hover:border-[var(--hover-border)] data-[state=open]:border-[var(--hover-border)] ${flashing ? "cat-flash" : ""} ${isLarge ? "text-[11px] px-2.5 py-1.5 border-solid border-[var(--hover-border)]" : "text-[10px] px-1.5 py-0.5 tracking-[0.04em]"}`}
          style={
            {
              background: bg,
              color,
              "--hover-border": color,
            } as React.CSSProperties
          }
        >
          {label}
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`opacity-[0.55] transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="p-1.5 rounded-xl min-w-[168px] bg-card border-[0.5px] border-border shadow-popover"
        align="start"
        role="listbox"
        aria-label="Change category"
      >
        <div className="text-[10px] tracking-[0.08em] px-2.5 pt-1.5 pb-1 text-muted-foreground">
          {isIncome ? "income type" : "change category"}
        </div>
        {categories.map((cat) => (
          <button
            key={cat}
            role="option"
            aria-selected={cat === currentCategory}
            onClick={() => handleSelect(cat)}
            className={`w-full flex items-center gap-2.5 text-[13px] font-medium px-2.5 py-1.5 rounded-lg text-left transition-colors text-foreground ${
              cat === currentCategory
                ? "bg-[var(--color-primary-bg)] hover:bg-[var(--color-primary-bg)]"
                : "bg-transparent hover:bg-[var(--surface-muted)]"
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0"
              style={{ background: (colorMap as Record<string, string>)[cat] }}
            />
            {(labelMap as Record<string, string>)[cat]}
            {cat === currentCategory && (
              <Check className="w-3.5 h-3.5 ml-auto text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
